/* global Office */
import React, { useEffect, useMemo, useState } from "react";
import "./pdfExport.css";
import { connectToTaskpane, sendToTaskpane } from "../../pdfExport/pdfDialogBridge";

const OPTIONS_KEY = "boq.pdfOptions";
const DEFAULT_OPTIONS = {
  orientation: "landscape",
  format: "a4",
  fontSize: 8,
  mono: false,
  toc: true,
  pageNumbers: true,
  withCover: true,
  author: "",
};

const isDetail = (name) => String(name).includes("დეტალური");

const loadOptions = () => {
  try {
    return { ...DEFAULT_OPTIONS, ...JSON.parse(window.localStorage.getItem(OPTIONS_KEY) || "{}") };
  } catch {
    return { ...DEFAULT_OPTIONS };
  }
};

function Seg({ value, options, onChange }) {
  return (
    <div className="seg">
      {options.map(([v, label]) => (
        <button key={String(v)} type="button" className={value === v ? "on" : ""} onClick={() => onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Root of #/pdf-export (an Office dialog). It has no Excel access: the taskpane sends the sheet list
 * and project data, and runs the export when "PDF ექსპორტი" is pressed here.
 */
export default function PdfExportApp() {
  const [phase, setPhase] = useState("loading"); // loading | ready | running | done | error
  const [error, setError] = useState("");
  const [progress, setProgress] = useState("");
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState("");
  const [opts, setOpts] = useState(loadOptions);
  const [cover, setCover] = useState({});
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    let cancelled = false;
    const handle = (msg) => {
      if (cancelled) return;
      if (msg.type === "init") {
        const names = msg.allNames || [];
        const root = msg.activeRoot && names.includes(msg.activeRoot) ? msg.activeRoot : null;
        const start = root
          ? [root, ...names.filter(isDetail)]
          : msg.activeName && names.includes(msg.activeName)
            ? [msg.activeName]
            : names;
        setData(msg);
        setSelected(start);
        setCover({ ...(msg.cover || {}), author: (msg.cover && msg.cover.author) || loadOptions().author });
        setFileName(msg.defaultFileName || "");
        setPhase("ready");
      } else if (msg.type === "init-error") {
        setError(msg.message || "ფურცლების ჩატვირთვა ვერ მოხერხდა");
        setPhase("error");
      } else if (msg.type === "progress") {
        setProgress(msg.text || "");
      } else if (msg.type === "result") {
        if (msg.ok) {
          setSummary(msg.summary);
          setPhase("done");
        } else {
          setError(msg.message || "უცნობი შეცდომა");
          setPhase("error");
        }
      }
    };
    const ready = typeof Office !== "undefined" && Office.onReady ? Office.onReady() : Promise.resolve();
    ready
      .then(() => connectToTaskpane(handle))
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "ფანჯარა ვერ გაიხსნა");
        setPhase("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setOpt = (key, value) => setOpts((o) => ({ ...o, [key]: value }));
  const setCoverField = (key, value) => setCover((c) => ({ ...c, [key]: value }));

  const names = data?.allNames || [];
  const q = filter.trim().toLowerCase();
  const visible = useMemo(() => names.filter((n) => !q || n.toLowerCase().includes(q)), [names, q]);
  const mainNames = visible.filter((n) => !isDetail(n));
  const detailNames = visible.filter(isDetail);

  const toggle = (name) => setSelected((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));
  const setGroup = (list, on) =>
    setSelected((s) => (on ? [...s, ...list.filter((n) => !s.includes(n))] : s.filter((n) => !list.includes(n))));

  const run = () => {
    try {
      window.localStorage.setItem(OPTIONS_KEY, JSON.stringify({ ...opts, author: cover.author || "" }));
    } catch {
      /* ignore */
    }
    setPhase("running");
    setProgress("");
    setError("");
    sendToTaskpane({
      type: "export",
      sheets: names.filter((n) => selected.includes(n)), // workbook order
      options: {
        orientation: opts.orientation,
        format: opts.format,
        fontSize: opts.fontSize,
        mono: opts.mono,
        toc: opts.toc,
        pageNumbers: opts.pageNumbers,
        fileName,
        cover: opts.withCover ? cover : null,
      },
    });
  };
  const close = () => sendToTaskpane({ type: "close" });

  const renderGroup = (title, list) =>
    list.length > 0 && (
      <div className="group">
        <div className="ghead">
          <b>{title}</b>
          <span>
            <button type="button" className="lnk" onClick={() => setGroup(list, true)}>ყველა</button>
            <button type="button" className="lnk" onClick={() => setGroup(list, false)}>მოხსნა</button>
          </span>
        </div>
        {list.map((n) => (
          <label key={n} className={selected.includes(n) ? "row on" : "row"}>
            <input type="checkbox" checked={selected.includes(n)} onChange={() => toggle(n)} />
            <span>{n}</span>
          </label>
        ))}
      </div>
    );

  const field = (key, label) => (
    <label className="fld" key={key}>
      <span>{label}</span>
      <input value={cover[key] ?? ""} onChange={(e) => setCoverField(key, e.target.value)} disabled={!opts.withCover} />
    </label>
  );

  return (
    <div className="pdfx" data-no-translate="true">
      <div className="phead">
        <b>PDF ექსპორტი</b>
        <button type="button" className="ic" onClick={close} aria-label="დახურვა">✕</button>
      </div>

      {phase === "loading" && <div className="pcenter">ფურცლების კითხვა…</div>}

      {phase === "running" && (
        <div className="pcenter">
          <div className="spin" />
          <div>PDF გენერაცია მიმდინარეობს…</div>
          {progress && <div className="muted">{progress}</div>}
        </div>
      )}

      {phase === "error" && (
        <div className="pcenter">
          <div className="msg err">{error}</div>
          <div className="acts">
            {data && <button type="button" className="btn" onClick={() => setPhase("ready")}>‹ უკან</button>}
            <button type="button" className="btn" onClick={close}>დახურვა</button>
          </div>
        </div>
      )}

      {phase === "done" && (
        <div className="pcenter">
          <div className="okmark">✓</div>
          <div><b>PDF მზადაა</b></div>
          <div className="muted">
            {summary?.fileName} · {summary?.selectedCount} ფურცელი · {summary?.pages} გვერდი
          </div>
          <div className="muted">ფაილი ავტომატურად ჩამოიტვირთა.</div>
          <div className="acts">
            <button type="button" className="btn" onClick={() => setPhase("ready")}>კიდევ ერთი ექსპორტი</button>
            <button type="button" className="btn pri" onClick={close}>დახურვა</button>
          </div>
        </div>
      )}

      {phase === "ready" && (
        <>
          <div className="pbody">
            <section className="col">
              <h4>ფურცლები <span className="count">{selected.length} / {names.length}</span></h4>
              <input className="search" placeholder="ძებნა…" value={filter} onChange={(e) => setFilter(e.target.value)} />
              <div className="presets">
                <button type="button" className="lnk" onClick={() => setSelected(names)}>ყველა</button>
                <button type="button" className="lnk" onClick={() => setSelected(names.filter((n) => !isDetail(n)))}>მხოლოდ ძირითადი</button>
                <button type="button" className="lnk" onClick={() => setSelected(names.filter(isDetail))}>მხოლოდ დეტალური</button>
                <button type="button" className="lnk" onClick={() => setSelected([])}>არცერთი</button>
              </div>
              <div className="list">
                {renderGroup("ძირითადი ფურცლები", mainNames)}
                {renderGroup("დეტალური ფურცლები", detailNames)}
                {!visible.length && <div className="muted pad">არაფერი მოიძებნა</div>}
              </div>
            </section>

            <section className="col">
              <h4>გვერდი</h4>
              <div className="opt">
                <span>ორიენტაცია</span>
                <Seg value={opts.orientation} onChange={(v) => setOpt("orientation", v)} options={[["landscape", "ჰორიზონტალური"], ["portrait", "ვერტიკალური"]]} />
              </div>
              <div className="opt">
                <span>ზომა</span>
                <Seg value={opts.format} onChange={(v) => setOpt("format", v)} options={[["a4", "A4"], ["a3", "A3"]]} />
              </div>
              <div className="opt">
                <span>შრიფტი</span>
                <Seg value={Number(opts.fontSize)} onChange={(v) => setOpt("fontSize", v)} options={[[7, "პატარა"], [8, "ნორმალური"], [9, "დიდი"], [10, "ძალიან დიდი"]]} />
              </div>
              <div className="opt">
                <span>ფერები</span>
                <Seg value={opts.mono} onChange={(v) => setOpt("mono", v)} options={[[false, "ფერადი"], [true, "შავ-თეთრი"]]} />
              </div>
              <label className="chk"><input type="checkbox" checked={opts.toc} onChange={(e) => setOpt("toc", e.target.checked)} /> სარჩევი გვერდებით</label>
              <label className="chk"><input type="checkbox" checked={opts.pageNumbers} onChange={(e) => setOpt("pageNumbers", e.target.checked)} /> გვერდების ნომრები ქვემოთ</label>

              <h4>სატიტულო გვერდი</h4>
              <label className="chk"><input type="checkbox" checked={opts.withCover} onChange={(e) => setOpt("withCover", e.target.checked)} /> დამკვეთისთვის (პროექტი, ჯამები, 1 მ² ღირებულება)</label>
              <div className="grid">
                {field("projectName", "პროექტი")}
                {field("customer", "დამკვეთი")}
                {field("location", "მისამართი")}
                {field("area", "ფართობი (მ²)")}
                {field("docNumber", "დოკუმენტის №")}
                {field("date", "თარიღი")}
                {field("author", "შეადგინა")}
              </div>

              <h4>ფაილი</h4>
              <label className="fld">
                <span>სახელი</span>
                <input value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="BOQ" />
              </label>
            </section>
          </div>

          <div className="pfoot">
            <span className="muted">{selected.length ? `${selected.length} ფურცელი ექსპორტისთვის` : "აირჩიეთ მინიმუმ ერთი ფურცელი"}</span>
            <span className="acts">
              <button type="button" className="btn" onClick={close}>გაუქმება</button>
              <button type="button" className="btn pri" disabled={!selected.length} onClick={run}>PDF ექსპორტი</button>
            </span>
          </div>
        </>
      )}
    </div>
  );
}
